import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { Check } from "lucide-react";
import { useNavigate, useLocation } from "react-router";
import { Input } from "../../primitives/Input";
import { Button } from "../../primitives/Button";
import { useAuth } from "../../provider";
import { AuthLayout } from "./AuthLayout";
export function ResetPasswordForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const { authClient } = useAuth();
    const { email = "", code = "" } = location.state ?? {};
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [strength, setStrength] = useState({
        level: 0,
        label: "Weak",
        color: "#EF4444",
    });
    const [requirements, setRequirements] = useState({
        length: false,
        uppercase: false,
        number: false,
        special: false,
    });
    useEffect(() => {
        const newRequirements = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        };
        setRequirements(newRequirements);
        const metCount = Object.values(newRequirements).filter(Boolean).length;
        let newStrength;
        if (metCount <= 1) {
            newStrength = { level: metCount, label: "Weak", color: "#EF4444" };
        }
        else if (metCount === 2) {
            newStrength = { level: 2, label: "Fair", color: "#F59E0B" };
        }
        else if (metCount === 3) {
            newStrength = { level: 3, label: "Good", color: "#FBBF24" };
        }
        else {
            newStrength = { level: 4, label: "Strong", color: "#10B981" };
        }
        setStrength(newStrength);
    }, [password]);
    const allRequirementsMet = Object.values(requirements).every(Boolean);
    const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
    const canSubmit = allRequirementsMet && passwordsMatch;
    const handleSetPassword = async () => {
        setIsLoading(true);
        setError(null);
        try {
            await authClient.resetPassword(email, code, password);
            navigate("/auth/reset-password/success");
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to reset password");
        }
        finally {
            setIsLoading(false);
        }
    };
    return (_jsxs(AuthLayout, { children: [_jsx("h1", { className: "text-duvi-text-primary mb-6", style: {
                    fontSize: "var(--text-xl)",
                    fontWeight: "var(--font-weight-semibold)",
                    lineHeight: "var(--line-height-heading)",
                }, children: "Set a new password" }), _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "block text-duvi-text-primary mb-2", style: { fontSize: "var(--text-sm)" }, children: "Password" }), _jsx(Input, { type: "password", value: password, onChange: (e) => setPassword(e.target.value), placeholder: "Enter your new password", "data-testid": "reset-password-new" })] }), _jsxs("div", { className: "mb-6", children: [_jsx("div", { className: "flex gap-1 mb-2", children: [1, 2, 3, 4].map((level) => (_jsx("div", { className: "h-1 flex-1 rounded-full transition-all", style: {
                                backgroundColor: strength.level >= level ? strength.color : "#1A1A24",
                            } }, level))) }), _jsx("div", { className: "text-duvi-text-secondary", style: { fontSize: "var(--text-xs)" }, children: strength.label })] }), _jsxs("div", { className: "mb-6", children: [_jsx("label", { className: "block text-duvi-text-primary mb-2", style: { fontSize: "var(--text-sm)" }, children: "Confirm password" }), _jsx(Input, { type: "password", value: confirmPassword, onChange: (e) => setConfirmPassword(e.target.value), placeholder: "Re-enter your new password", "data-testid": "reset-password-confirm" })] }), _jsxs("div", { className: "mb-6 space-y-2", children: [_jsx(RequirementItem, { met: requirements.length, children: "At least 8 characters" }), _jsx(RequirementItem, { met: requirements.uppercase, children: "One uppercase letter" }), _jsx(RequirementItem, { met: requirements.number, children: "One number" }), _jsx(RequirementItem, { met: requirements.special, children: "One special character" })] }), error && (_jsx("div", { className: "mb-4 text-duvi-destructive", style: { fontSize: "var(--text-sm)" }, children: error })), _jsx(Button, { variant: "primary", className: "w-full", onClick: handleSetPassword, disabled: !canSubmit || isLoading, "data-testid": "reset-password-submit", children: isLoading ? "Setting password..." : "Set password" })] }));
}
function RequirementItem({ met, children, }) {
    return (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: `w-4 h-4 rounded-full flex items-center justify-center transition-colors ${met ? "bg-duvi-success" : "bg-duvi-elevated"}`, children: met && _jsx(Check, { className: "w-3 h-3 text-duvi-bg" }) }), _jsx("span", { className: met ? "text-duvi-text-primary" : "text-duvi-text-muted", style: { fontSize: "var(--text-sm)" }, children: children })] }));
}
//# sourceMappingURL=ResetPasswordForm.js.map